import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, Menu, X, ChevronDown, LogOut, Search } from 'lucide-react';
import logo from '../assets/logo.png';

const tabs = [
  { id: 'CAF', label: 'CAF' },
  { id: 'PATRONATO', label: 'PATRONATO' },
  { id: 'IMMIGRAZIONE', label: 'IMMIGRAZIONE' },
  { id: 'ALTRI SERVIZI', label: 'ALTRI SERVIZI' }
];

const navItems = [
  { label: 'HOME', path: '/' },
  { label: 'SERVIZI', path: '/services', hasDropdown: true },
  { label: 'CHI SIAMO', path: '/about-us' },
  { label: 'CONTATTACI', path: '/contact-us' },
];

const yellowGradientCSS = {
  background: 'linear-gradient(135deg, #F2C231 0%, #C69A32 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const timeoutRef = useRef(null);
  const servicesTimeoutRef = useRef(null);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    let token;

    if (consent === 'true') {
      token = localStorage.getItem('jwtToken');
    } else {
      token = sessionStorage.getItem('jwtToken');
    }
    setIsLoggedIn(!!token);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (servicesTimeoutRef.current) {
        clearTimeout(servicesTimeoutRef.current);
      }
    };
  }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/services', { state: { searchQuery: searchQuery.trim() } });
      setSearchQuery('');
      setIsMenuOpen(false); // Add this line
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setIsDropdownOpen(false);
    navigate('/');
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleDropdown = (index) =>
    setActiveDropdown(activeDropdown === index ? null : index);

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current);
    }
    setIsServicesDropdownOpen(true);
  };

  const handleServicesMouseLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setIsServicesDropdownOpen(false);
    }, 200);
  };

  return (
    <nav className="bg-white shadow-md px-3 py-2 md:px-6 md:py-4 relative">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center space-x-2">
          <Link to="/">
            <img src={logo} alt="CAF Logo" className="w-16 md:w-24 h-auto object-contain" />
          </Link>
        </div>
        <form onSubmit={handleSearch} className="hidden md:flex items-center">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca servizi..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 w-[150%]"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </form>

        {/* Desktop Links */}
        <div className="hidden md:flex space-x-8 items-center">
          {navItems.map((item) => (
            <div key={item.label} className="relative">
              {item.hasDropdown ? (
                <div
                  className="relative"
                >
                  <button
                    className="text-gray-700 font-bold transition-colors relative flex items-center gap-1 focus:outline-none"
                    type="button"
                    onClick={() => setIsServicesDropdownOpen((prev) => !prev)}
                    onMouseEnter={() => setIsServicesDropdownOpen(true)}
                  >
                    SERVIZI
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${isServicesDropdownOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </button>


                  {isServicesDropdownOpen && (
                    <div
                      className="absolute left-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50"
                      onMouseLeave={() => setIsServicesDropdownOpen(false)}
                    >
                      {tabs.map((tab) => (
                        <Link
                          key={tab.id}
                          to="/services"
                          state={{ selectedCategory: tab.label }}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => setIsServicesDropdownOpen(false)}
                        >
                          {tab.label}
                        </Link>
                      ))}
                    </div>
                  )}

                </div>



              ) : (
                <Link
                  to={item.path}
                  className="text-gray-700 font-bold transition-colors relative"
                  onMouseEnter={e => {
                    e.currentTarget.style.background = yellowGradientCSS.background;
                    e.currentTarget.style.webkitBackgroundClip = yellowGradientCSS.WebkitBackgroundClip;
                    e.currentTarget.style.webkitTextFillColor = yellowGradientCSS.WebkitTextFillColor;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '';
                    e.currentTarget.style.webkitBackgroundClip = '';
                    e.currentTarget.style.webkitTextFillColor = '';
                  }}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          {/* Conditional rendering for User/Logout dropdown */}
          {isLoggedIn ? (
            <div className="relative">
              <button
                className="text-gray-700 hover:text-yellow-600 transition-colors focus:outline-none"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
              >
                <User className="w-6 h-6" />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  <button
                    onClick={() => {
                      navigate('/edit-profile');
                      setIsDropdownOpen(false);
                    }}
                    className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Modifica Profilo
                  </button>
                  <button
                    onClick={() => {
                      navigate('/update-password');
                      setIsDropdownOpen(false);
                    }}
                    className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Aggiorna Password
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Disconnetti
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-gray-700 hover:text-yellow-600 transition-colors">
                <User className="w-6 h-6" />
              </Link>
              <Link
                to="/signup"
                className="text-gray-700 font-bold hover:text-yellow-600 transition-colors"
                style={yellowGradientCSS}
              >
                REGISTRATI
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden">
          <button
            onClick={toggleMenu}
            className="flex items-center justify-center w-10 h-10 text-gray-700 hover:bg-gray-200 rounded-md"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-slate-800 backdrop-blur-md border-t border-white/10 z-50">
          <div className="px-4 py-6 space-y-4 text-left">
            {/* Mobile Search Bar */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca servizi..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-600 bg-slate-700 text-white placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </form>
            {navItems.map((item, index) => (
              <div key={item.label} className="flex flex-col items-start">
                <div className="flex items-center justify-between w-full">
                  <Link
                    to={item.path}
                    className="text-gray-300 hover:text-white transition-colors text-lg"
                    onClick={() => {
                      if (!item.hasDropdown) setIsMenuOpen(false);
                      else toggleDropdown(index);
                    }}
                  >
                    {item.label}
                  </Link>
                  {item.hasDropdown && (
                    <button
                      onClick={() => toggleDropdown(index)}
                      className="ml-2 p-2 text-gray-300 hover:text-white transition-colors"
                    >
                      <ChevronDown
                        size={16}
                        className={`transform transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''
                          }`}
                      />
                    </button>
                  )}
                </div>
                {/* Mobile Services Dropdown */}
                {item.hasDropdown && activeDropdown === index && (
                  <div className="ml-4 mt-2 space-y-2 w-full">
                    {tabs.map((tab) => (
                      <Link
                        key={tab.id}
                        to="/services"
                        state={{ selectedCategory: tab.label }}
                        className="block text-gray-400 hover:text-white transition-colors text-sm py-1"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {tab.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Conditional rendering for User/Logout in Mobile */}
            <div className="pt-4 border-t border-white/10 flex justify-start space-x-4">
              {isLoggedIn ? (
                <>
                  <Link to="/profile" className="text-gray-300">
                    <User className="w-6 h-6" />
                  </Link>
                  <Link
                    to="/edit-profile"
                    className="text-gray-300 hover:text-white transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Modifica Profilo
                  </Link>
                  <button onClick={handleLogout} className="text-gray-300 hover:text-white transition-colors">
                    <LogOut className="w-6 h-6" />
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-300">
                    <User className="w-6 h-6" />
                  </Link>
                  <Link to="/signup" className="text-gray-300 hover:text-white transition-colors">
                    REGISTRATI
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;